/**
 * Given a dollar amount and a tax percentage, calculate the tax of that dollar
 * amount and add it to the dollar amount.
 *
 * @param {number} total dollar amount
 * @param {number} tax tax amount percentage
 * @returns {number} new total with tax added
 */
export function calculateTax(total, tax) {
  return (parseFloat(tax) / 100) * total;
}

/**
 * Given a dollar amount and an option tax amount, calculate what the total would be
 * if tax is provided. Whether or not tax is provided, the return is a string
 * formatted to 2 decimal places.
 * e.g. given `("1000", "6")` it will return `"1060.00"`.
 * given `("1500")` it will return `"1500.00"`
 *
 * @param {number|string} total dollar amount
 * @param {number|string} tax optional tax amount
 * @returns {string} total formatted to 2 decimal places
 */
export function calculateTotalWithTax(total, tax = 0) {
  if (!total) {
    return '0';
  }

  let newTotal = parseFloat(total, 10);

  if (tax) {
    newTotal += calculateTax(newTotal, Number(tax));
  }

  return newTotal.toFixed(2);
}

/**
 * Given a string or number, returns a string version of that number formatted in
 * a language sensitive representation. e.g. given `"1234567"` will return
 * `"1,234,567"`.
 *
 * @param {string|number} value number to be formatted
 * @returns {string} formatted string
 */
export function formatWithCommas(value) {
  return Number(value).toLocaleString();
}

/**
 * Given 3 strings, returns a calculation of the commissionRate (set to a decimal)
 * multiplied by the referralRate (set to a decimal) multiplied by the
 * closingPrice
 *
 * @param {string} closingPrice entered closing price
 * @param {string} commissionRate entered commission rate, not decimal
 * @param {string} referralRate contracted referral rate, not decimal
 * @returns {sting} returns 0 if all params are missing or
 * returns formatted string
 */
export function calculateReferralFee(
  closingPrice,
  commissionRate,
  referralRate
) {
  if (!closingPrice || !commissionRate || !referralRate) {
    return '0';
  }

  const commissionRateCalc = parseFloat(commissionRate) / 100;
  const referralRateCalc = parseFloat(referralRate) / 100;
  const referralFeeCalc =
    parseFloat(closingPrice) * commissionRateCalc * referralRateCalc;

  return referralFeeCalc.toFixed(2);
}

/**
 * Given 2 strings, returns a calculation of the commissionRate (set to a decimal)
 * multiplied by the closingPrice to get the commission amount
 *
 * @param {string} closingPrice entered closing price
 * @param {string} commissionRate entered commission rate, not decimal
 * @returns {string} returns 0 if all params are missing or
 * returns calculated, formatted string
 */
export function calculateCommissionAmount(closingPrice, commissionRate) {
  if (!closingPrice || !commissionRate) {
    return '0';
  }

  const commissionAmountTotal =
    parseFloat(closingPrice) * (parseFloat(commissionRate) / 100);

  return commissionAmountTotal.toFixed(2);
}

/**
 * Given 2 strings, returns a calculation of the commissionAmount divided by
 * the closingPrice to get the commission rate set in percentage format
 *
 * @param {string} commissionAmount entered commission amount
 * @param {string} closingPrice entered closing price
 * @returns {string} returns 0 if all params are missing or
 * returns calculated string
 */
export function calculateCommissionRate(commissionAmount, closingPrice) {
  if (!commissionAmount || !closingPrice) {
    return '0';
  }

  if (closingPrice === '0' || closingPrice === '0.00') {
    return '0';
  }

  const commissionRateTotal =
    (parseFloat(commissionAmount) / parseFloat(closingPrice)) * 100;

  return String(commissionRateTotal);
}
